import { useLayoutEffect, useState, useEffect, useRef, useMemo } from "react";
import { Handle, NodeProps, Position, NodeToolbar } from "reactflow";
import { useStore } from "../App/store";
import "./FocusItem.css";
import { CiSquarePlus, CiSquareMinus } from "react-icons/ci";
import { BsCheckCircleFill, BsFillInfoCircleFill } from "react-icons/bs";
import { IoFlag } from "react-icons/io5";
import { NodeData } from "../types/types";
import { OverlayPanel } from "primereact/overlaypanel";
import { Tag } from "primereact/tag";
import ReactMarkdown from "react-markdown";
import { convertHtmlToMarkdown } from "../Helpers/NodeHelpers";
import WhyDialog from "./WhyDialog";

function FocusItem({ id, data, isConnectable, selected }: NodeProps<NodeData>) {
  const inputRef = useRef<HTMLInputElement>(null);
  const dragRef = useRef<HTMLInputElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const op = useRef<OverlayPanel>(null);
  const setSelectedNodeId = useStore((state) => state.setSelectedNodeId);
  const isNodeSelected = useStore((state) => state.selectedNodeId) === id;
  const presentationOrderInputRef = useRef<HTMLInputElement>(null);
  const priorityInputRef = useRef<HTMLInputElement>(null);
  const valueInputRef = useRef<HTMLInputElement>(null);
  const setShowNotesModal = useStore((state) => state.setShowNotesModal);
  const setCurrentNotes = useStore((state) => state.setCurrentNotes);
  const setCurrentLinks = useStore((state) => state.setCurrentLinks);
  const setCurrentNodeLabel = useStore((state) => state.setCurrentNodeLabel);
  const [showDetails, setShowDetails] = useState(false);
  const [showWhyDialog, setShowWhyDialog] = useState(false);
  const user = useStore((state) => state.user);
  const showWhyTag = user?.settings?.showWhyTag !== false; // Default to true
  const nodes = useStore((state) => state.nodes);
  const edges = useStore((state) => state.edges);
  const updateNodeData = useStore((state) => state.updateNodeData);

  // Check if this is a root node (no incoming edges)
  const isRootNode = !edges.some((edge) => edge.target === id);

  useLayoutEffect(() => {
    if (inputRef.current && wrapperRef.current) {
      wrapperRef.current.style.width = inputRef.current.style.width;
    }
  }, [inputRef.current?.style.width]);

  const updateNodeLabel = useStore((state) => state.updateNodeLabel);
  const updatePresentationOrder = useStore(
    (state) => state.updatePresentationOrder
  );
  const updatePriority = useStore((state) => state.updatePriority);
  const updateValue = useStore((state) => state.updateValue);
  const focusValue = useStore((state) => state.focusValue);
  const cursorPositionRef = useRef<number | null>(null);
  const isPresentationMode = useStore((state) => state.isPresentationMode);
  const presentWithShowHideButtons = useStore(
    (state) => state.presentWithShowHideButtons
  );

  const handleFocus = (event: { target: { select: () => any } }) => {
    setSelectedNodeId(id);
    event.target.select();
  };

  const handleBlur = () => {
    setSelectedNodeId(null);
  };

  useEffect(() => {
    setTimeout(() => {
      inputRef.current?.focus({ preventScroll: true });
    }, 1);
  }, []);

  useLayoutEffect(() => {
    // console.log("focusValue", focusValue);
    if (inputRef.current) {
      const minWidth = 50; // Minimum width in pixels
      const padding = 10; // Padding to add some space around the text

      inputRef.current.setAttribute("spellcheck", "false");

      // Create a canvas element to measure the text width
      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d");
      if (context) {
        context.font = getComputedStyle(inputRef.current).font;
        const textWidth = context.measureText(inputRef.current.value).width;
        inputRef.current.style.width = `${Math.max(
          minWidth,
          textWidth + padding
        )}px`;
        if (dragRef.current) {
          dragRef.current.style.width = `${Math.max(
            minWidth,
            textWidth + padding
          )}px`;
        }
      }
    }
  }, [data.label]);

  const handleChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    cursorPositionRef.current = evt.target.selectionStart;
    updateNodeLabel(id, evt.target.value);
  };

  const handleKeyDown = (evt: React.KeyboardEvent<HTMLInputElement>) => {
    // Cmd+Enter (Mac) or Ctrl+Enter to create child nodes
    if (evt.key === "Enter" && (evt.metaKey || evt.ctrlKey)) {
      evt.preventDefault();

      // Get current state from store
      const store = useStore.getState();
      const tabModeActive = store.tabModeActive;
      const tabModeParentId = store.tabModeParentId;

      // Determine the parent node for tab mode
      let parentNodeId = id;

      if (tabModeActive && tabModeParentId) {
        // Continue with existing parent
        parentNodeId = tabModeParentId;
      } else {
        // Start new tab mode with current node as parent
        store.setTabModeActive(true);
        store.setTabModeParentId(id);
      }

      // Create new child node
      const newNodeId = store.addChildNodeInTabMode(parentNodeId);

      // Redistribute all children evenly
      store.redistributeChildNodes(parentNodeId);

      // Focus the new node after a brief delay
      setTimeout(() => {
        const newInput = document.querySelector(
          `[node-identifier="${newNodeId}"]`
        ) as HTMLInputElement;
        if (newInput) {
          newInput.focus({ preventScroll: true });
          newInput.select();
        }
      }, 50);
    } else if (evt.key === "Escape") {
      // Stop Tab mode on ESC key
      const store = useStore.getState();
      if (store.tabModeActive) {
        evt.preventDefault();
        store.setTabModeActive(false);
        store.setTabModeParentId(null);
      }
    }
  };

  const handlePresentationOrder = (
    evt: React.ChangeEvent<HTMLInputElement>
  ) => {
    cursorPositionRef.current = evt.target.selectionStart;
    const parsedValue = parseInt(evt.target.value, 10);
    const value = isNaN(parsedValue) ? "" : parsedValue.toString();
    updatePresentationOrder(id, value);
  };

  const handlePriorityChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    cursorPositionRef.current = evt.target.selectionStart;
    const value = evt.target.value;
    const isValid =
      value === "" || /^[A-Za-z]\d*$/.test(value) || /^\d+$/.test(value);
    if (isValid) {
      updatePriority(id, value);
    }
  };

  const handleValueChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    cursorPositionRef.current = evt.target.selectionStart;
    const parsedValue = evt.target.value;
    updateValue(id, parsedValue);
  };

  useEffect(() => {
    if (cursorPositionRef.current !== null && inputRef.current) {
      inputRef.current.selectionStart = cursorPositionRef.current;
      inputRef.current.selectionEnd = cursorPositionRef.current;
    }
  }, [data.label]);

  const hideChildNodes = useStore((state) => state.hideChildNodes);
  const showChildNodes = useStore((state) => state.showChildNodes);
  const [displayedNodeValue, setDisplayedNodeValue] = useState<string[]>([]);
  const doRollup = useStore((state) => state.doRollup);
  const [isNegativeValue, setIsNegativeValue] = useState(false);
  const activateShowValues = useStore((state) => state.showValues);

  useEffect(() => {
    //console.log("showNodeValues", activateShowValues);
    const displayValues: string[] = [];
    if (doRollup) {
      const rollupValues = data.rollupValues || [];
      if (rollupValues) {
        rollupValues.forEach((rollup) => {
          if (rollup && rollup.title && rollup.value.length > 0) {
            displayValues.push(rollup.title + ": " + rollup.value);
          }
        });
      }
      setDisplayedNodeValue(displayValues);
    } else {
      const values = data.values || [];
      if (values) {
        values.forEach((value) => {
          if (value && value.title && value.value.length > 0) {
            displayValues.push(value.title + ": " + value.value);
          }
        });
      }
      setDisplayedNodeValue(displayValues);
    }
  }, [data, activateShowValues, doRollup]);

  const hasHiddenChildren = useMemo(
    () => nodes && nodes.some((node) => node.parentId === id && node.hidden),
    [nodes, id]
  );
  const hasVisibleChildren = useMemo(
    () => nodes.some((node) => node.parentId === id && !node.hidden),
    [nodes, id]
  );

  const handleHideShowClick = (event: React.MouseEvent) => {
    const showAllLevels = event.shiftKey;
    if (hasHiddenChildren) {
      showChildNodes(id, showAllLevels);
    } else {
      hideChildNodes(id);
      setTimeout(() => {
        document.getSelection()?.removeAllRanges();
        inputRef.current?.blur();
        // divInputRef.current!.style.visibility = 'visible';
      }, 0);
    }
  };

  function handleDetailsClick(event: React.MouseEvent<Element>): void {
    event.stopPropagation();
    setShowDetails((prevShowDetails) => !prevShowDetails);
  }

  const handleSaveWhy = (whyText: string) => {
    updateNodeData(id, { ...data, why: whyText });
  };

  return (
    <div key={id}>
      {(!isPresentationMode || presentWithShowHideButtons) && (
        <div>
          {hasVisibleChildren && !hasHiddenChildren && (
            <div className="focus-show-hide-area">
              <CiSquareMinus
                className="focus-show-hide-icon focus-show-hide-icon-minus"
                onClick={handleHideShowClick}
              />
            </div>
          )}
          {hasHiddenChildren && (
            <div className="focus-show-hide-area">
              <CiSquarePlus
                className="focus-show-hide-icon focus-show-hide-icon-plus"
                onClick={handleHideShowClick}
              />
            </div>
          )}
        </div>
      )}

      <div className="focus-presentation-order-area">
        {focusValue === "presentation" && (
          <input
            id="presentation-order-input-field"
            className="focus-presentation-order-input-field"
            spellCheck="false"
            value={data.presentationOrder}
            onChange={handlePresentationOrder}
            onFocus={handleFocus}
            ref={presentationOrderInputRef}
          />
        )}
      </div>

      {focusValue === "priorities" ? (
        <div className="focus-priority-area">
          <input
            id="priority-input-field"
            className="focus-priority-input-field"
            spellCheck="false"
            value={data.priority}
            onChange={handlePriorityChange}
            onFocus={handleFocus}
            ref={priorityInputRef}
          />
        </div>
      ) : (
        data.priority &&
        (focusValue === "prioritiesView" || focusValue === "all") && (
          <div className="focus-priority-display">{data.priority}</div>
        )
      )}

      {focusValue === "rollup" ? (
        <div className="focus-rollup-area">
          <input
            id="rollup-input-field"
            className="focus-rollup-input-field"
            spellCheck="false"
            value={data.value}
            onChange={handleValueChange}
            onFocus={handleFocus}
            ref={valueInputRef}
          />
        </div>
      ) : (
        activateShowValues && (
          <div
            className={`focus-rollup-display ${
              isNegativeValue ? "negative" : ""
            }`}
          >
            <div className="focus-rollup-container">
              {displayedNodeValue.map((value, index) => (
                <div
                  key={index}
                  className={
                    doRollup ? "focus-rollup-value" : "focus-regular-value"
                  }
                >
                  {value}
                </div>
              ))}
            </div>
          </div>
        )
      )}

      {useMemo(
        () => (
          <div
            style={{
              backgroundColor:
                focusValue == "all" ? data.backgroundColor : "white",
            }}
            ref={dragRef}
            className={`dragHandle focus-dragHandle draghandle-focus ${
              isNodeSelected ? "selected" : ""
            } draghandle-color-standard`}
          />
        ),
        [data.backgroundColor, focusValue, isNodeSelected]
      )}

      <div ref={wrapperRef} className="focus-inputWrapper">
        <div className="inputArea-focus">
          {data.icon && data.icon.length > 0 ? (
            // Check if icon is a URL (http/https), a base64 string, or a file extension
            /^https?:\/\//.test(data.icon) ||
            data.icon.endsWith(".png") ||
            data.icon.endsWith(".jpg") ||
            data.icon.endsWith(".jpeg") ||
            data.icon.startsWith("data:image/") ? (
              <div
                className="custom-node-icon dragHandle"
                style={{
                  top: "-12px",
                  left: "-30px",
                  height: "30px",
                  width: "30px",
                  borderRadius: "50%",
                  border: "1px solid rgb(18, 102, 86)",
                  backgroundColor: "gainsboro",
                  overflow: "hidden",
                }}
              >
                <img
                  className="image"
                  src={data.icon}
                  alt="node-icon"
                  style={{
                    height: "100%",
                    width: "100%",
                    objectFit: "cover",
                  }}
                  onClick={(e) => op.current?.toggle(e)}
                />
                <OverlayPanel ref={op}>
                  <img src={data.icon} style={{ width: "300px" }} />
                </OverlayPanel>
              </div>
            ) : (
              <div
                className="custom-node-icon dragHandle"
                style={{
                  top: "-12px",
                  left: "-30px",
                  height: "30px",
                  width: "30px",
                  borderRadius: "50%",
                  backgroundColor: "white",
                  border: "1px solid rgb(18, 102, 86)",
                  alignContent: "center",
                  justifyContent: "center",
                  display: "flex",
                  alignItems: "center",
                  justifyItems: "center",
                  alignSelf: "center",
                  overflow: "hidden",
                }}
              >
                <div
                  className={`custom-node-icon dragHandle`}
                  style={{
                    fontSize: "1.6rem",
                    top: "-0.7px",
                    left: "2.3px",
                    width: "100%",
                  }}
                >
                  {data.icon}
                </div>
              </div>
            )
          ) : (
            <></>
          )}
          <input
            id="focus-input-field"
            className="focus-input"
            node-identifier={id}
            spellCheck="false"
            autoComplete="off"
            style={{ color: focusValue !== "all" ? "black" : data.color }}
            value={data.label}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onClick={() => setSelectedNodeId(id)}
            ref={inputRef}
          />
          {/* Notes icon - positioned inside node on the left */}
          {data.notes && (focusValue === "notes" || focusValue === "all") && (
            <div className="focus-notes-indicator-left">
              <BsFillInfoCircleFill
                style={{
                  cursor: "pointer",
                  fontSize: "0.7rem",
                  color:
                    data.notes && data.notes.length > 0
                      ? "#126656"
                      : "undefined",
                }}
                onMouseEnter={(e) => {
                  // Show details after 1 second hover
                  const target = e.currentTarget as unknown as HTMLElement & {
                    __hoverTimeout?: ReturnType<typeof setTimeout>;
                  };
                  target.__hoverTimeout = setTimeout(
                    () => handleDetailsClick(e),
                    1000
                  );
                }}
                onMouseLeave={(e) => {
                  // Cancel timer and hide details
                  const target = e.currentTarget as unknown as HTMLElement & {
                    __hoverTimeout?: ReturnType<typeof setTimeout> | null;
                  };
                  if (target.__hoverTimeout) {
                    clearTimeout(target.__hoverTimeout);
                    target.__hoverTimeout = null;
                  }
                  setShowDetails(false);
                }}
                onDoubleClick={() => {
                  setSelectedNodeId(id);
                  setCurrentNodeLabel(data.label);
                  setCurrentLinks(data.links);
                  setCurrentNotes(data.notes);
                  setShowNotesModal(true);
                }}
              />
            </div>
          )}
          {/* Flag icon - positioned above node */}
          {data.flagged &&
            data.flagged !== "none" &&
            (focusValue === "marker" || focusValue === "all") && (
              <div className="focus-icons-row">
                <IoFlag
                  className="focus-flag-icon"
                  style={{
                    cursor: "pointer",
                    fontSize: "0.8rem",
                    color:
                      data.flagged === "green"
                        ? "#8a9a5b"
                        : data.flagged === "yellow"
                        ? "#d2b773"
                        : data.flagged === "red"
                        ? "#b76e79"
                        : "#126656",
                  }}
                />
              </div>
            )}
          {data.complete && (focusValue === "done" || focusValue === "all") && (
            <div className="focus-completed-indicator">
              <BsCheckCircleFill color="#a8ac5e" />
            </div>
          )}
        </div>
      </div>

      <Handle
        type="target"
        position={Position.Top}
        isConnectable={isConnectable}
      />
      <Handle
        type="source"
        position={Position.Top}
        isConnectable={isConnectable}
      />

      {/* Why tag for root nodes */}
      {isRootNode && showWhyTag && (
        <div
          style={{
            position: "absolute",
            bottom: "-14px",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 10,
            cursor: "pointer",
          }}
          onDoubleClick={(e) => {
            e.stopPropagation();
            setShowWhyDialog(true);
          }}
        >
          <Tag
            value={data.why ? "why✓" : "why?"}
            style={{
              fontSize: "0.375rem",
              padding: "0.125rem 0.25rem",
              backgroundColor: data.why ? "rgb(18, 102, 86)" : "#4C566B",
              color: "#ffffff",
              border: "none",
            }}
          />
        </div>
      )}

      {/* Add custom handles for cross-links */}
      <Handle
        type="source"
        position={Position.Right}
        id="cross-link"
        style={{ background: "red" }}
      />
      <Handle
        type="target"
        position={Position.Left}
        id="cross-link"
        style={{ background: "blue" }}
      />

      {showDetails && (
        <div className="focus-node-details-area">
          <div className="focus-node-details-label">
            <ReactMarkdown
              components={{
                h1: ({ node, ...props }) => (
                  <h1
                    style={{
                      fontSize: "2em",
                      marginTop: "0em",
                      marginBottom: "0em",
                      paddingTop: "0em",
                      paddingBottom: "0em",
                    }}
                    {...props}
                  />
                ),
                h2: ({ node, ...props }) => (
                  <h2
                    style={{
                      fontSize: "1.5em",
                      marginTop: "0em",
                      marginBottom: "0em",
                      paddingTop: "0em",
                      paddingBottom: "0em",
                    }}
                    {...props}
                  />
                ),
                h3: ({ node, ...props }) => (
                  <h3
                    style={{
                      fontSize: "1.2em",
                      marginTop: "0em",
                      marginBottom: "0em",
                      paddingTop: "0em",
                      paddingBottom: "0em",
                    }}
                    {...props}
                  />
                ),
                h4: ({ node, ...props }) => (
                  <h4
                    style={{
                      fontSize: "1.1em",
                      marginTop: "0em",
                      marginBottom: "0em",
                      paddingTop: "0em",
                      paddingBottom: "0em",
                    }}
                    {...props}
                  />
                ),
                p: ({ node, ...props }) => (
                  <p
                    style={{
                      fontSize: "1em",
                      marginTop: "0em",
                      marginBottom: "0.3em",
                      paddingTop: "0em",
                      paddingBottom: "0em",
                    }}
                    {...props}
                  />
                ),
                ul: ({ node, ...props }) => (
                  <ul
                    style={{
                      fontSize: "1em",
                      margin: "0em",
                      paddingInlineStart: "1em", // reduced from 1.2em to 1em
                      listStylePosition: "outside",
                    }}
                    {...props}
                  />
                ),
                ol: ({ node, ...props }) => (
                  <ol
                    style={{
                      fontSize: "1em",
                      margin: "0em",
                      paddingInlineStart: "1em", // reduced from 1.2em to 1em
                      listStylePosition: "outside",
                    }}
                    {...props}
                  />
                ),
                li: ({ node, ...props }) => (
                  <li
                    style={{
                      fontSize: "1em",
                      margin: "0em",
                      paddingInlineStart: "0em", // set to 0 to minimize bullet gap
                    }}
                    {...props}
                  />
                ),
                input: ({ node, ...props }) =>
                  props.type === "checkbox" ? (
                    <input
                      style={{
                        fontSize: "1em",
                        margin: "0em",
                        paddingInlineStart: "0em",
                      }}
                      {...props}
                    />
                  ) : (
                    <input {...props} />
                  ),
                table: () => null,
                thead: () => null,
                tbody: () => null,
                tr: () => null,
                th: () => null,
                td: () => null,
              }}
            >
              {convertHtmlToMarkdown(data.notes)}
            </ReactMarkdown>
          </div>
        </div>
      )}

      {/* Why Dialog */}
      <WhyDialog
        visible={showWhyDialog}
        initialWhy={data.why || ""}
        onSave={handleSaveWhy}
        onHide={() => setShowWhyDialog(false)}
      />
    </div>
  );
}

export default FocusItem;
